import { ReinitDTO } from '../infrastructure/state-service.service';
import { SeatSelectionType } from './checkout';
import { Show } from './show';

export abstract class Step {
  readonly title: string;
  readonly route: string;
  skipped = false;
  canBeNavigatedTo: (state: ReinitDTO, show: Show) => boolean;
}

export class SeatsSelectionStep extends Step {
  readonly title = 'Select seats';
  readonly route = 'seats-selection';
  readonly canBeNavigatedTo = (state: ReinitDTO) => {
    if (this.skipped) return false;

    return SeatSelectionType.TICKETS_FIRST !== state.seatSelectionType;
  };
}

export class SeatsSelectionByTicketsStep extends Step {
  readonly title = 'Select seats';
  readonly route = 'seats';
  readonly canBeNavigatedTo = (state: ReinitDTO) => {
    if (this.skipped) return false;

    return state.cart.summary?.products?.length > 0;
  };
}
export class TicketProductAssigmentStep extends Step {
  readonly title = 'Products';
  readonly route = 'overview';
  readonly canBeNavigatedTo = (state: ReinitDTO) => {
    if (this.skipped) return false;

    return state.cart.summary?.products?.length > 0;
  };
}

export class TicketsSelectionStep extends Step {
  readonly title = 'Products';
  readonly route = 'tickets';
  readonly canBeNavigatedTo = (state: ReinitDTO) => {
    if (this.skipped) return false;

    return SeatSelectionType.TICKETS_FIRST === state.seatSelectionType;
  };
}

export class SupplementsStep extends Step {
  readonly title = 'Supplements';
  readonly route = 'supplements';
  readonly canBeNavigatedTo = (state: ReinitDTO) => {
    if (this.skipped) return false;

    /* atLeastOneSupplementAvailable  is checked in component onInit*/
    return state.cart.summary?.products?.length > 0;
  };
}

export class ReservingStep extends Step {
  readonly title = 'Reserving';
  readonly route = 'reserving';
  readonly canBeNavigatedTo = (state: ReinitDTO) => {
    if (this.skipped) return false;
    return state.cart.summary?.products?.length > 0 && state.requiresReservation;
  };
}

export class InformationStep extends Step {
  readonly title = 'Information';
  readonly route = 'information';
  readonly canBeNavigatedTo = (state: ReinitDTO, show: Show) => {
    if (this.skipped) return false;
    return state.cart.summary?.products?.length > 0 && (state.selectedSeats.size > 0 || show.disableAssignedSeating);
  };
}

export class PaymentStep extends Step {
  readonly title = 'Payment';
  readonly route = 'payment';
  readonly canBeNavigatedTo = (state: ReinitDTO, show: Show) => {
    const userInfoValid = !!state.cart.buyer.email && !!state.cart.buyer.first_name && !!state.cart.buyer.last_name;

    return (
      state.cart.summary?.products?.length > 0 &&
      (state.selectedSeats.size > 0 || show.disableAssignedSeating) &&
      userInfoValid &&
      (state.cart.summary.pending_amount > 0 || (state.cart.summary.gift_cards?.length ?? 0) > 0)
    );
  };
}

export class AddonsStep extends Step {
  readonly title = 'Addons';
  readonly route = 'add-ons';
  readonly canBeNavigatedTo = (state: ReinitDTO) => {
    if (this.skipped) return false;

    const atLeastOneAddonAvailable = true;
    return state.cart.summary?.products?.length > 0 && atLeastOneAddonAvailable;
  };
}

export class SubscriptionPeriodSelectionStep extends Step {
  readonly title = 'Subscription period';
  readonly route = 'period-selection';
  readonly canBeNavigatedTo = (state: ReinitDTO) => {
    return true;
  };
}
/**
 * The catalogue step of the prepaid flow: every subscription the POS sells, arranged into its
 * product groups, next to the gift cards. A recurring shop walks {@link SubscriptionRecurringSelectionStep}
 * instead — it has already picked its plan on the period step and only confirms it.
 */
export class SubscriptionSelectionStep extends Step {
  readonly title = 'Subscriptions';
  readonly route = 'subs-selection';
  readonly canBeNavigatedTo = (state: ReinitDTO) => {
    return true;
  };
}

/**
 * The recurring flow's own selection step. It follows the period step, so the plan, its billing
 * period and a first reserved cart line already exist by the time it opens: it shows that one
 * subscription and lets the buyer change nothing but the quantity, which cannot go below one.
 */
export class SubscriptionRecurringSelectionStep extends Step {
  readonly title = 'Recurring subscription';
  readonly route = 'rec-subs-selection';
  readonly canBeNavigatedTo = (state: ReinitDTO) => {
    return true;
  };
}
export class SubscriptionCreateAccountStep extends Step {
  readonly title = 'Subscriptions create account';
  readonly route = 'subs-create-account';
  readonly canBeNavigatedTo = (state: ReinitDTO) => {
    return true;
  };
}
export class SubscriptionConfirmationStep extends Step {
  readonly title = 'Select subscription start date';
  readonly route = 'subs-confirmation';
  readonly canBeNavigatedTo = (state: ReinitDTO) => {
    return true;
  };
}
